import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMeetingNote,
  deleteMeetingNote,
  ingestMeetingNote,
  listMeetingNotes,
  reIngestMeetingNote,
  syncMeetingNoteToKb,
  updateMeetingNote,
  type IngestMeetingNoteInput,
  type IngestStatus,
  type MeetingNoteInput,
  type MeetingNoteOwner,
} from '../lib/meetingNotesApi';

// ---------------------------------------------------------------------------
// Meeting Notes hooks (CLE-205). Polymorphic across client/project/lead — the
// owner identity is folded into the query key so the three contexts cache
// independently and invalidate precisely.
// ---------------------------------------------------------------------------

const MEETING_NOTES_KEY = ['meeting-notes'] as const;

function ownerKey(owner: MeetingNoteOwner): string {
  if (owner.clientId) return `client:${owner.clientId}`;
  if (owner.projectId) return `project:${owner.projectId}`;
  if (owner.leadId) return `lead:${owner.leadId}`;
  if (owner.personId) return `person:${owner.personId}`;
  if (owner.meetingId) return `meeting:${owner.meetingId}`;
  return 'unknown';
}

function ownerEnabled(owner: MeetingNoteOwner): boolean {
  return Boolean(
    owner.clientId || owner.projectId || owner.leadId || owner.personId || owner.meetingId,
  );
}

// --- Queries ---------------------------------------------------------------

export function useMeetingNotes(
  owner: MeetingNoteOwner,
  filters: { ingestStatus?: IngestStatus } = {},
) {
  return useQuery({
    queryKey: [...MEETING_NOTES_KEY, ownerKey(owner), filters],
    queryFn: () => listMeetingNotes(owner, filters),
    enabled: ownerEnabled(owner),
    // Surface ingest-status transitions (PENDING/PROCESSING -> COMPLETED)
    // without a manual refresh while the background pipeline runs.
    refetchInterval: (query) => {
      const notes = query.state.data;
      if (!notes) return false;
      const active = notes.some(
        (n) => n.ingestStatus === 'PENDING' || n.ingestStatus === 'PROCESSING',
      );
      return active ? 5000 : false;
    },
  });
}

// --- Mutations -------------------------------------------------------------

function invalidate(qc: ReturnType<typeof useQueryClient>, owner: MeetingNoteOwner) {
  void qc.invalidateQueries({ queryKey: [...MEETING_NOTES_KEY, ownerKey(owner)] });
}

export function useCreateMeetingNote(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingNoteInput) => createMeetingNote(owner, input),
    onSuccess: () => invalidate(qc, owner),
  });
}

export function useIngestMeetingNote(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IngestMeetingNoteInput) => ingestMeetingNote(owner, input),
    onSuccess: () => invalidate(qc, owner),
  });
}

export function useUpdateMeetingNote(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { noteId: string; input: Partial<MeetingNoteInput> }) =>
      updateMeetingNote(vars.noteId, vars.input),
    onSuccess: () => invalidate(qc, owner),
  });
}

export function useDeleteMeetingNote(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteMeetingNote(noteId),
    onSuccess: () => invalidate(qc, owner),
  });
}

export function useReIngestMeetingNote(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => reIngestMeetingNote(noteId),
    onSuccess: () => invalidate(qc, owner),
  });
}

export function useSyncMeetingNoteToKb(owner: MeetingNoteOwner) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => syncMeetingNoteToKb(noteId),
    onSuccess: () => invalidate(qc, owner),
  });
}
